import { supabase } from "@/lib/supabase";
import { setGmailToken } from "./tokenUtils";

interface GmailUserData {
  email: string;
  name?: string;
}

export const handleGmailAuth = async (
  userData: GmailUserData,
  accessToken: string,
  expiresIn: number,
  customName?: string // Add customName parameter
) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('No authenticated user found');

    const expiryTime = Date.now() + (expiresIn * 1000);
    setGmailToken(userData.email, accessToken, expiryTime);

    // Check if account exists - using maybeSingle() instead of single()
    const { data: existingAccount, error: checkError } = await supabase
      .from('email_accounts')
      .select('*')
      .eq('email', userData.email)
      .maybeSingle();

    // Log the check results
    console.log('Checking for existing account:', { existingAccount, checkError });

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking for existing account:', checkError);
      throw checkError;
    }

    // Use customName if provided, otherwise use Gmail name
    const displayName = customName || userData.name || userData.email;

    if (existingAccount) {
      console.log('Updating existing account:', existingAccount.email);
      // Update existing account
      const { error: updateError } = await supabase
        .from('email_accounts')
        .update({
          name: displayName,
          credentials: {
            access_token: accessToken,
            expires_at: expiryTime
          }
        })
        .eq('id', existingAccount.id);

      if (updateError) {
        console.error('Error updating account:', updateError);
        throw updateError;
      }
    } else {
      console.log('Creating new account for:', userData.email);
      // Insert new account
      const { error: insertError } = await supabase
        .from('email_accounts')
        .insert({
          user_id: user.id,
          email: userData.email,
          name: displayName,
          provider: 'gmail',
          credentials: {
            access_token: accessToken,
            expires_at: expiryTime
          }
        });

      if (insertError) {
        console.error('Error inserting new account:', insertError);
        throw insertError;
      }
    }

    console.log('Gmail account connected successfully:', userData.email);
    return { email: userData.email, credentials: { access_token: accessToken, expires_in: expiresIn } };
  } catch (error) {
    console.error('Error in Gmail auth:', error);
    throw error;
  }
};